<script setup>
import { ref, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { PhPlus } from '@phosphor-icons/vue'

import Modal       from '@pages/Shared/Modal.vue'
import Input       from '@pages/Shared/form/Input.vue'
import Select      from '@pages/Shared/form/Select.vue'
import MultiSelect from '@pages/Shared/form/MultiSelect.vue'
import Spinner     from '@pages/Shared/ui/Spinner.vue'

import { flashToast } from '@/toast.js'
import { capitalize } from '@/helpers.js'
import { usePromotion } from '@pages/Composables/usePromotion.js'

const props = defineProps({
    modelValue:     { type: Boolean, required: true },
    sections:       { type: Array,   required: true },
    sessions:       { type: Array,   required: true },
    sessionOptions: { type: Array,   required: true },
    sectionOptions: { type: Array,   required: true },
})

const emit = defineEmits(['update:modelValue', 'success'])

const {
    getSectionClasses,
    getNextClass,
    getAvailableSubjects,
    changeSubject,
    commitSubject,
    cancelAddSubject,
    startAddSubject,
    removeSubject,
} = usePromotion(props.sections)

const expandedRuleId = ref(null)
const previewOpen    = ref(false)
const classOptions   = ref([])

const form = useForm({
    section_id:      null,
    from_session_id: null,
    to_session_id:   null,
    from_classes:    [],
    promotion_rules: [],
    class_rooms:     [],
})

watch(() => form.section_id, (val) => {
    const classes        = getSectionClasses(val)
    classOptions.value   = classes.map(c => ({ label: c.name, value: c.id }))
    form.from_classes    = classes.map(c => c.id)
    form.promotion_rules = []
    expandedRuleId.value = null
})

watch(() => form.from_classes, (newClasses) => {
    const classes = getSectionClasses(form.section_id)
    if (!classes.length) return

    newClasses.forEach(classId => {
        if (form.promotion_rules.some(r => r.from_class_id === classId)) return
        const cls = classes.find(c => c.id === classId)
        if (!cls) return
        const nextClass = getNextClass(cls)
        form.promotion_rules.push({
            from_class_id:   cls.id,
            from_class_name: cls.name,
            to_class_id:     nextClass.id,
            to_class_name:   nextClass.name,
            min_average:     40,
            subjects:        [],
            addingSubject:   false,
            draft:           { subject_id: null, min_score: 40 },
            errors:          {},
        })
    })

    form.promotion_rules = form.promotion_rules.filter(r => newClasses.includes(r.from_class_id))

    if (!expandedRuleId.value && form.promotion_rules.length) {
        expandedRuleId.value = form.promotion_rules[0].from_class_id
    }
}, { deep: true })

const toggleRuleCard = (classId) => {
    expandedRuleId.value = expandedRuleId.value === classId ? null : classId
}

const removeRule = (classId) => {
    form.from_classes = form.from_classes.filter(id => id !== classId)
}

const handleSubmit = () => {
    form.class_rooms = form.promotion_rules.map(rule => ({
        from_class_id: rule.from_class_id,
        to_class_id:   rule.to_class_id,
        average_score: rule.min_average,
        subject_rules: rule.subjects.map(s => ({ subject_id: s.id, min_score: s.min_score })),
    }))

    form.post('/staff/manage-promotions', {
        onSuccess: (page) => {
            close()
            emit('success')
            flashToast(page.props.flash)
        },
        onError: () => {
            flashToast({ type: 'error', message: 'Please check the form for errors' })
        },
    })
}

const close = () => {
    emit('update:modelValue', false)
    expandedRuleId.value = null
    previewOpen.value    = false
    form.reset()
}
</script>

<template>
    <Modal :model-value="modelValue" title="New Promotion" size="medium" @close="close">
        <form class="p-6" @submit.prevent="handleSubmit">
            <p class="text-sm text-gray-500 mb-6">
                Select the classes to promote and define the rules for each class.
            </p>

            <div class="grid grid-cols-2 gap-4 mb-4">
                <Select label="From Session" v-model="form.from_session_id"
                    :options="sessionOptions" placeholder="Select session" :error="form.errors.from_session_id" />
                <Select label="To Session" v-model="form.to_session_id"
                    :options="sessionOptions" placeholder="Select session" :error="form.errors.to_session_id" />
            </div>

            <div class="space-y-4 mb-6">
                <Select label="Section" v-model="form.section_id"
                    :options="sectionOptions" placeholder="Select section" :error="form.errors.section_id" />
                <MultiSelect label="From Classes" v-model="form.from_classes"
                    :options="classOptions" placeholder="Select classes" :error="form.errors.from_classes" />
            </div>

            <div class="space-y-3">
                <div v-for="(rule, index) in form.promotion_rules" :key="rule.from_class_id"
                    class="border border-gray-200 rounded-lg overflow-hidden">

                    <div class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
                        @click="toggleRuleCard(rule.from_class_id)">
                        <div class="flex items-center gap-2.5">
                            <span class="w-6 h-6 flex items-center justify-center bg-primary text-white text-xs font-bold rounded-full shrink-0">
                                {{ index + 1 }}
                            </span>
                            <span class="text-sm font-semibold text-gray-800">
                                {{ capitalize(rule.from_class_name) }}
                                <span class="text-gray-400 mx-1">→</span>
                                <span :class="rule.to_class_name === 'Graduation' ? 'text-edit' : 'text-gray-800'">
                                    {{ capitalize(rule.to_class_name) }}
                                </span>
                            </span>
                        </div>
                        <button type="button" class="text-gray-300 hover:text-red-500 text-xl leading-none transition"
                            @click.stop="removeRule(rule.from_class_id)">&times;</button>
                    </div>

                    <div v-show="expandedRuleId === rule.from_class_id"
                        class="px-4 pb-4 pt-3 border-t border-gray-100 space-y-4 bg-gray-50/50">

                        <div>
                            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                                Minimum Average Score
                            </label>
                            <Input type="number" v-model="rule.min_average" min="0" max="100" placeholder="0" />
                            <p v-if="rule.errors?.average_score" class="text-xs text-red-500 mt-1">{{ rule.errors.average_score }}</p>
                        </div>

                        <div class="space-y-2">
                            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide block">
                                Subject Minimum Scores
                            </label>

                            <div v-for="(subject, si) in rule.subjects" :key="subject.id" class="flex items-center gap-2">
                                <Select
                                    :model-value="subject.id"
                                    :options="getAvailableSubjects(form.section_id, rule.from_class_id, rule.subjects, subject.id)"
                                    class="flex-1"
                                    @update:modelValue="changeSubject(rule, si, $event, form.section_id)" />
                                <Input type="number" v-model="subject.min_score" class="w-20" min="0" max="100" />
                                <button type="button" class="text-gray-300 hover:text-red-500 text-xl leading-none transition"
                                    @click="removeSubject(rule, subject.id)">&times;</button>
                            </div>

                            <p v-if="rule.errors?.subject_rules" class="text-xs text-red-500">{{ rule.errors.subject_rules }}</p>

                            <div v-if="rule.addingSubject" class="flex items-center gap-2">
                                <Select v-model="rule.draft.subject_id"
                                    :options="getAvailableSubjects(form.section_id, rule.from_class_id, rule.subjects)"
                                    placeholder="Select subject" class="flex-1"
                                    @update:modelValue="commitSubject(rule, form.section_id)" />
                                <Input type="number" v-model="rule.draft.min_score" class="w-20" min="0" max="100" />
                                <button type="button" class="text-gray-300 hover:text-red-500 text-xl leading-none transition"
                                    @click="cancelAddSubject(rule)">&times;</button>
                            </div>

                            <button v-if="!rule.addingSubject" type="button"
                                class="text-xs font-medium text-primary hover:text-primary-light flex items-center gap-1 mt-1 transition"
                                @click="startAddSubject(rule, form.section_id)">
                                <PhPlus :size="14" /> Add Subject Threshold
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="previewOpen && form.promotion_rules.length"
                class="mt-5 border border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 class="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Promotion Preview</h3>
                <div v-for="rule in form.promotion_rules" :key="rule.from_class_id"
                    class="mb-3 last:mb-0 p-3 bg-white rounded-lg border border-gray-100">
                    <div class="flex items-center justify-between">
                        <div class="text-sm font-medium text-gray-800">
                            {{ capitalize(rule.from_class_name) }}
                            <span class="text-gray-400 mx-1">→</span>
                            <span v-if="rule.to_class_name !== 'Graduation'">{{ capitalize(rule.to_class_name) }}</span>
                            <span v-else class="px-2 py-0.5 text-xs font-semibold bg-green-50 text-edit rounded-full border border-green-100">Graduation</span>
                        </div>
                        <span class="text-xs text-gray-400">Avg ≥ {{ rule.min_average }}</span>
                    </div>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                        <span v-for="subject in rule.subjects" :key="subject.id"
                            class="px-2 py-0.5 text-xs bg-primary-50 text-primary rounded-md border border-primary-100">
                            {{ subject.name }} ≥ {{ subject.min_score }}
                        </span>
                        <span v-if="!rule.subjects.length" class="text-xs text-gray-400 italic">No subject rules</span>
                    </div>
                </div>
            </div>

            <div class="flex items-center gap-3 pt-5 border-t border-gray-100 mt-6">
                <button type="button" @click="previewOpen = !previewOpen"
                    :disabled="!form.promotion_rules.length"
                    class="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-light
                           rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed">
                    {{ previewOpen ? 'Hide Preview' : 'Show Preview' }}
                </button>
                <button type="submit" :disabled="form.processing || !form.promotion_rules.length"
                    class="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-edit hover:bg-edit-light
                           rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed
                           flex justify-center items-center gap-2">
                    <Spinner v-if="form.processing" :size="14" /> Promote
                </button>
            </div>
        </form>
    </Modal>
</template>